import { Box, CircularProgress, IconButton } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import { useCheckUserPermission } from "../../../../api/hooks/useCheckUserPermission";
import useColors from "../../../../api/hooks/useColors";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { jiraIssuesKeys } from "../../../../api/queryKeys";
import CustomToolTipWithPermission from "../../../../components/CustomToolTipWithPermission";
import { PLATFORM_USER_ACTIONS } from "../../../../utils/constants";
import IntegrationImage from "../components/IntegrationImage";

export default function JiraCreateMultipleIssuesButton({
  fullControl,
  selectedMisconfigurations,
  onClick,
  loading,
}) {
  const colors = useColors();
  const { trackEvent, getMisconfigurationsWithTickets } = useAPI();
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("jira");

  const { permitted: hasPermission } = useCheckUserPermission(
    PLATFORM_USER_ACTIONS.MODIFY_TICKET
  );
  const [allHaveTickets, setAllHaveTickets] = useState(false);

  const {
    data: misconfigurationIdsWithTickets,
    isSuccess: misconfigurationIdsWithTicketsIsSuccess,
  } = useQuery({
    queryKey: jiraIssuesKeys.jiraMisconfigurationsWithTickets(
      fullControl?.control_id
    ),
    queryFn: () => getMisconfigurationsWithTickets(fullControl?.control_id),
    select: (response) => response.data,

    enabled: fullControl !== null && isIntegrated && !isIntegratedLoading,
  });

  const handleClick = () => {
    const message = `User clicked on 'Create multiple Jira issues' button for ${selectedMisconfigurations} misconfigurations: ${selectedMisconfigurations}`;
    trackEvent(message);
    onClick();
  };

  // Check if all selected misconfigurations already have tickets
  useEffect(() => {
    if (misconfigurationIdsWithTicketsIsSuccess) {
      const newAllHaveTickets =
        selectedMisconfigurations?.length > 0 &&
        selectedMisconfigurations.every((misconfigId) =>
          misconfigurationIdsWithTickets.includes(misconfigId)
        );

      if (newAllHaveTickets !== allHaveTickets) {
        setAllHaveTickets(newAllHaveTickets);
      }
    }
  }, [
    selectedMisconfigurations,
    misconfigurationIdsWithTicketsIsSuccess,
    misconfigurationIdsWithTickets,
  ]);

  const isDisabled =
    loading ||
    isIntegratedLoading ||
    !hasPermission ||
    selectedMisconfigurations?.length === 0 ||
    allHaveTickets;

  return (
    <CustomToolTipWithPermission
      title={
        !hasPermission
          ? "You don't have permission to create Jira issues"
          : selectedMisconfigurations?.length === 0
          ? "Select misconfigurations to create multiple Jira issues"
          : allHaveTickets
          ? "All selected misconfigurations already have Jira tickets"
          : "Create multiple Jira issues"
      }
      placement="top"
      permission={hasPermission}
    >
      <Box>
        <IconButton
          sx={{
            height: "32px",
            width: "32px",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            borderRadius: "10px",
            border: "1px solid " + colors.enabled_stroke,
            "&:hover": {
              background: colors.grey_8percent,
            },
            "&.Mui-disabled": {
              cursor: "not-allowed",
              "& > *": {
                opacity: 0.6,
              },
            },
            "&, &.Mui-disabled": {
              backgroundColor: colors.common_white,
            },
          }}
          onClick={handleClick}
          disabled={isDisabled}
          disableRipple={isDisabled}
        >
          {loading ? (
            <CircularProgress size="1rem" sx={{ color: colors.primary_main }} />
          ) : (
            <IntegrationImage
              name={"jira"}
              width={"16px"}
              newLogo={true}
              showToolTip={false}
            />
          )}
        </IconButton>
      </Box>
    </CustomToolTipWithPermission>
  );
}
